import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);


  //https://desi-mart-backend-3mil.onrender.com/api
  //https://desimartbackend-production.up.railway.app/
  //http://localhost:5000/api  // https://api.desimart.net/api
  const API_URL = "https://api.desimart.net/api";
  
  // Setup axios interceptor for token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        setInitialized(true);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        if (response.data.success) {
          const meUser = response.data.data.user;
          setUser({
            ...meUser,
            _id: meUser._id || meUser.id,
            id: meUser.id || meUser._id,
            wallet: {
              ...meUser.wallet,
              repurchaseWallet: meUser.wallet?.repurchaseWallet || 0,
              depositWallet: meUser.wallet?.depositWallet || 0,
              mainWallet: meUser.wallet?.mainWallet || 0
            }
          });
        } else {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
        }
        setError(null);
      } catch (err) {
        console.error('Failed to load user:', err);
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    loadUser();
  }, [API_URL]);

  // ========== REGISTER FUNCTION ==========
  const register = useCallback(async (name, email, phone, password, sponsorCode) => {
    setError(null);
    setLoading(true);

    try {
      // Validate sponsor code is provided
      if (!sponsorCode) {
        throw new Error('Sponsor Code is required to join the system!');
      }

      const response = await axios.post(`${API_URL}/auth/register`, {
        name,
        email,
        phone,
        password,
        sponsorCode: sponsorCode.toUpperCase()
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        // Store token
        // localStorage.setItem('token', token);
        // axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // // Set user
        // setUser(user);
        
        return { 
          success: true, 
          user,
          message: `REGISTRATION SUCCESSFUL! WELCOME ${user.name}! YOUR LOGIN ID: ${user.mySponsorCode} AND PASSWORD: ${password}. PLEASE LOGIN TO CONTINUE.`
        };
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // ========== LOGIN FUNCTION ==========
  const login = useCallback(async (loginId, password) => {
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        loginId,
        password
      });

      if (response.data.success) {
        const { token, user: loggedUser } = response.data.data;
        
        // Store token
        localStorage.setItem('token', token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Normalize user object so both id and _id exist, and wallet is fully initialized with repurchaseWallet
        const normalizedUser = {
          ...loggedUser,
          _id: loggedUser._id || loggedUser.id,
          id: loggedUser.id || loggedUser._id,
          wallet: {
            ...loggedUser.wallet,
            repurchaseWallet: loggedUser.wallet?.repurchaseWallet || 0,
            depositWallet: loggedUser.wallet?.depositWallet || 0,
            mainWallet: loggedUser.wallet?.mainWallet || 0
          }
        };

        // Set user immediately
        setUser(normalizedUser);

        // Fetch /auth/me in background to ensure all relationships and wallet subdocuments are fully synced
        axios.get(`${API_URL}/auth/me`)
          .then((meRes) => {
            if (meRes.data?.success && meRes.data?.data?.user) {
              const meUser = meRes.data.data.user;
              setUser({
                ...meUser,
                _id: meUser._id || meUser.id,
                id: meUser.id || meUser._id,
                wallet: {
                  ...meUser.wallet,
                  repurchaseWallet: meUser.wallet?.repurchaseWallet || 0,
                  depositWallet: meUser.wallet?.depositWallet || 0,
                  mainWallet: meUser.wallet?.mainWallet || 0
                }
              });
            }
          })
          .catch((meErr) => {
            console.warn('Post-login /auth/me sync:', meErr);
          });
        
        return { success: true, user: normalizedUser };
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // ========== LOGOUT FUNCTION ==========
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setError(null);
  }, []);

  // ========== CHECK SPONSOR CODE ==========
  const checkSponsorCode = useCallback(async (code) => {
    try {
      const response = await axios.get(`${API_URL}/auth/check-sponsor/${code}`);
      return response.data;
    } catch (err) {
      return { success: false, valid: false };
    }
  }, [API_URL]);

  // ========== REFRESH USER DATA ==========
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      if (response.data.success) {
        const meUser = response.data.data.user;
        setUser({
          ...meUser,
          _id: meUser._id || meUser.id,
          id: meUser.id || meUser._id,
          wallet: {
            ...meUser.wallet,
            repurchaseWallet: meUser.wallet?.repurchaseWallet || 0,
            depositWallet: meUser.wallet?.depositWallet || 0,
            mainWallet: meUser.wallet?.mainWallet || 0
          }
        });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return false;
    }
  }, [API_URL]);

  // ========== GET DOWNLINE TREE ==========
  const getDownlineTree = useCallback(async (sponsorCode, depth = 5) => {
    try {
      const response = await axios.get(`${API_URL}/auth/get-sponsor-tree/${sponsorCode}?depth=${depth}`);
      return response.data;
    } catch (err) {
      return { success: false, error: 'Failed to fetch downline' };
    }
  }, [API_URL]);

  const value = {
    user,
    loading,
    error,
    initialized,
    register,
    login,
    logout,
    refreshUser,
    checkSponsorCode,
    getDownlineTree,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};